import { t } from '../strings';
import { EDIT_FIELDS, type EditField } from '../pages/recordPages';
import { formatPhone } from './phone';
import type { DuplicateRecordType } from './duplicateReport';
import type { MergeTarget } from './mergeMap';

/**
 * The merge step's per-field choice and its preview (piece 10; decision 1491).
 *
 * 🔒 The fields the step asks about are the record's own form fields, read off
 * `EDIT_FIELDS`, the one list the Company and Person forms draw. A field joins
 * the step the day it joins the form. The few left out are named below with
 * the reason each is settled somewhere else.
 *
 * 🔒 Where both records fill a field and the values differ, the step asks, and
 * the survivor's value is picked first. Where only the merged record fills it,
 * the survivor takes it without asking. Where only the survivor fills it,
 * nothing changes.
 */
export const MERGE_FIELD_EXCLUSIONS: Readonly<Record<MergeTarget, Readonly<Record<string, string>>>> = {
  CompanyRecord: {
    isCustomer: 'the survivor carries every flag either company carried (decision 0637)',
    isVendor: 'the survivor carries every flag either company carried (decision 0637)',
    isManufacturer: 'the survivor carries every flag either company carried (decision 0637)',
    defaultBillingSiteId: 'a site, not a field: the merged company’s sites move to the survivor and are reopened there',
    ein: 'a sealed number the step never reads as text; the survivor keeps its own (decision 3082)',
    dunsNumber: 'a sealed number the step never reads as text; the survivor keeps its own (decision 3082)',
  },
  PersonRecord: {
    status: 'a move to Deceased ends every employment and sends a notice, through its own confirm (decision 0169)',
  },
  // Decision 7470: an Item merge asks about the Item form's own fields, less these four.
  Item: {
    sku: 'unique: the item that stays keeps its own part number, and the merged one resolves to it through RecordMerge',
    type: 'a Good holds stock and a Service holds none, so the item that stays keeps its own type',
    unitId: 'two items that count in different units are refused, so the unit never differs',
    replacedByItemId: 'the merge rewrites the replacement chain itself: a link to the merged item names the one that stays',
  },
};

/** The fields the merge step reads and writes for a record type: the form's own, less the exclusions above. */
export function mergeFields(recordType: MergeTarget): readonly EditField[] {
  const excluded = MERGE_FIELD_EXCLUSIONS[recordType];
  return EDIT_FIELDS[recordType].filter((field) => !(field.key in excluded));
}

/** One record's values for the merge step's fields, keyed as the form keys them. */
export type MergeFieldValues = Readonly<Record<string, unknown>>;

/** A field holds a value: an empty or blank string, an empty list and an absent value do not. A boolean always does. */
export function mergeFieldFilled(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function sameValue(a: unknown, b: unknown): boolean {
  if (typeof a === 'string' && typeof b === 'string') return a.trim() === b.trim();
  return JSON.stringify(a) === JSON.stringify(b);
}

/** A field both records fill with different values: the one the step asks about. */
export interface MergeFieldConflict {
  key: string;
  label: string;
  field: EditField;
  survivor: unknown;
  merged: unknown;
}

/** 🔒 The fields the step lists: both records fill them and the values differ. */
export function mergeFieldConflicts(recordType: MergeTarget, survivor: MergeFieldValues, merged: MergeFieldValues): MergeFieldConflict[] {
  return mergeFields(recordType)
    .filter((field) => mergeFieldFilled(survivor[field.key]) && mergeFieldFilled(merged[field.key]) && !sameValue(survivor[field.key], merged[field.key]))
    .map((field) => ({ key: field.key, label: field.label, field, survivor: survivor[field.key], merged: merged[field.key] }));
}

/** 🔒 The fields the survivor takes without asking: only the merged record fills them, and the survivor shows them. */
export function mergeFieldsTakenSilently(recordType: MergeTarget, survivor: MergeFieldValues, merged: MergeFieldValues): string[] {
  return Object.keys(mergeFieldPatch(recordType, survivor, merged, []));
}

/** The keys a merge request names that are not among the step's fields; the merge refuses them. */
export function unknownMergeFields(recordType: MergeTarget, keys: readonly string[]): string[] {
  const known = new Set(mergeFields(recordType).map((field) => field.key));
  return keys.filter((key) => !known.has(key));
}

/**
 * What the merge writes to the survivor: every field only the merged record
 * fills, and each listed field the person picked the merged record's value
 * for. A pick on a field the merged record leaves empty writes nothing, so a
 * merge never blanks the survivor.
 *
 * 🔒 A field the form hides behind another, the reason a company is exempt
 * from finance charges behind its flag, is written only where the survivor
 * shows it once the merge has run (found in review, 2026-10-01). A boolean
 * always counts as filled, so the flag is never taken without asking, and a
 * reason taken alone would sit on a survivor that is not exempt.
 */
export function mergeFieldPatch(recordType: MergeTarget, survivor: MergeFieldValues, merged: MergeFieldValues, fromMerged: readonly string[]): Record<string, unknown> {
  const picked = new Set(fromMerged);
  const patch: Record<string, unknown> = {};
  const fields = mergeFields(recordType);
  for (const field of fields) {
    const theirs = merged[field.key];
    if (!mergeFieldFilled(theirs)) continue;
    const ours = survivor[field.key];
    if (!mergeFieldFilled(ours) || (picked.has(field.key) && !sameValue(ours, theirs))) patch[field.key] = theirs;
  }
  for (const field of fields) {
    const behind = field.hiddenWhen;
    if (!behind || !(field.key in patch)) continue;
    const after = behind.key in patch ? patch[behind.key] : survivor[behind.key];
    if (behind.oneOf.includes(String(after ?? ''))) delete patch[field.key];
  }
  return patch;
}

/** A field's value as the step and the History row show it: Yes or No, an option's own label, a phone as dialled. */
export function mergeFieldText(field: EditField, value: unknown): string {
  if (!mergeFieldFilled(value)) return t('duplicates.none');
  if (typeof value === 'boolean') return value ? t('record.yes') : t('record.no');
  const option = field.options?.find((one) => one.value === value);
  if (option) return option.label;
  if (field.kind === 'phone' && typeof value === 'string') return formatPhone(value);
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

/**
 * The History row's detail for the values a merge wrote (decision 1491): each
 * field by its label, with the survivor's value before and the value taken.
 * Null where the row carries none.
 */
export function mergeFieldsHistoryDetail(recordType: string, changedFields: unknown): string | null {
  if ((recordType !== 'CompanyRecord' && recordType !== 'PersonRecord' && recordType !== 'Item') || !Array.isArray(changedFields) || changedFields.length === 0) return null;
  const fields = mergeFields(recordType);
  const parts = (changedFields as { field?: unknown; from?: unknown; to?: unknown }[]).map((change) => {
    const field = fields.find((one) => one.key === change.field);
    if (!field) return null;
    return `${field.label}: ${mergeFieldText(field, change.from)} → ${mergeFieldText(field, change.to)}`;
  });
  const shown = parts.filter((part): part is string => part !== null);
  return shown.length > 0 ? shown.join(' · ') : null;
}

/** 🔒 The preview names this many rows of each kind, and the count of the rest (decision 1491). */
export const MERGE_PREVIEW_CAP = 20;

/**
 * The kinds the preview names: a company's documents, sites and people; a
 * person's documents and employments; an item's stock, document lines, bill of
 * materials lines, supplier rows, and prices and cost history (decision 7470).
 */
export type MergeMoveKind = 'documents' | 'sites' | 'people' | 'employments' | 'stock' | 'documentLines' | 'bomLines' | 'suppliers' | 'prices';

export interface MergeMoveRow {
  id: string;
  label: string;
}

/** One kind of row that moves, named up to the cap. */
export interface MergeMoveList {
  kind: MergeMoveKind;
  names: MergeMoveRow[];
  /** How many more move beyond the names shown. */
  more: number;
}

/** What the merge step reads before the press: both records' field values, and what moves, from a dry pass of the map walk. */
export interface MergePreviewAnswer {
  recordType: DuplicateRecordType;
  losingId: string;
  winningId: string;
  survivor: MergeFieldValues;
  merged: MergeFieldValues;
  moves: MergeMoveList[];
  /** The press goes to an approver rather than merging at once, so a picked value on it waits for the approval (found in review, 2026-10-01; decision 1603). */
  goesToApprover: boolean;
}

/** A kind's full list of rows, cut to the cap with the count of the rest. */
export function capMoveList(kind: MergeMoveKind, rows: readonly MergeMoveRow[]): MergeMoveList {
  return { kind, names: rows.slice(0, MERGE_PREVIEW_CAP), more: Math.max(0, rows.length - MERGE_PREVIEW_CAP) };
}
